import { registerBlockType } from '@wordpress/blocks';
import { __ } from '@wordpress/i18n';
import { InspectorControls, useBlockProps } from '@wordpress/block-editor';
import { PanelBody, Placeholder, Spinner, TextControl, RangeControl, ToggleControl } from '@wordpress/components';
import { ColorPalette } from '@wordpress/block-editor';
import { useEffect, useState } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { Icon, payment, cart } from '@wordpress/icons';

import './editor.css';
import './style.css';

registerBlockType('tixfox/event-button', {
	edit: ({ attributes, setAttributes }) => {
		const { eventSlug, eventTitle, buttonText, backgroundColor, textColor, buttonSize, borderRadius, showIcon } = attributes;
		const [events, setEvents] = useState(null);
		const [loading, setLoading] = useState(true);
		const [error, setError] = useState(null);
		const [activeTab, setActiveTab] = useState('live');
		const blockProps = useBlockProps();

		useEffect(() => {
			apiFetch({ path: '/tixfox/v1/events' })
				.then((data) => {
					setEvents(data);
					setLoading(false);
				})
				.catch((err) => {
					setError(err.message);
					setLoading(false);
				});
		}, []);

		const renderEventsList = (eventsList, category) => {
			if (!eventsList || eventsList.length === 0) return null;

			return (
				<div className="tixfox-events-category">
					<h4>{category}</h4>
					<div className="tixfox-events-list">
						{eventsList.map((event) => (
							<div
								key={event.slug}
								className={`tixfox-event-item ${eventSlug === event.slug ? 'selected' : ''}`}
								onClick={() => {
									setAttributes({
										eventSlug: event.slug,
										eventTitle: event.title,
									});
								}}
							>
								{event.event_image && (
									<img
										src={`https://api.tixfox.co/storage/${event.event_image}`}
										alt={event.title}
										className="tixfox-event-image"
									/>
								)}
								<div className="tixfox-event-info">
									<h5>{event.title}</h5>
									<p className="tixfox-event-date">
										{new Date(event.start_time).toLocaleDateString()}
									</p>
									<p className="tixfox-event-location">{event.address}</p>
								</div>
							</div>
						))}
					</div>
				</div>
			);
		};

		return (
			<div {...blockProps}>
				<InspectorControls>
					<PanelBody title={__('Event Selection', 'tixfox')} initialOpen={true}>
						{loading && (
							<div className="tixfox-loading">
								<Spinner />
								<p>{__('Loading events...', 'tixfox')}</p>
							</div>
						)}

						{error && (
							<div className="tixfox-error">
								<p>{__('Error loading events:', 'tixfox')} {error}</p>
								<p>{__('Please configure your API key in TixFox settings.', 'tixfox')}</p>
							</div>
						)}

						{!loading && !error && events && (
							<div className="tixfox-events-panel">
								<div className="tixfox-tabs">
									<button
										className={`tixfox-tab ${activeTab === 'live' ? 'active' : ''}`}
										onClick={() => setActiveTab('live')}
									>
										{__('Live', 'tixfox')}
									</button>
									<button
										className={`tixfox-tab ${activeTab === 'upcoming' ? 'active' : ''}`}
										onClick={() => setActiveTab('upcoming')}
									>
										{__('Upcoming', 'tixfox')}
									</button>
									<button
										className={`tixfox-tab ${activeTab === 'past' ? 'active' : ''}`}
										onClick={() => setActiveTab('past')}
									>
										{__('Past', 'tixfox')}
									</button>
								</div>

								<div className="tixfox-tab-content">
									{activeTab === 'live' && (
										events.live_events?.length > 0 ? (
											<div className="tixfox-events-list">
												{events.live_events.map((event) => (
													<div
														key={event.slug}
														className={`tixfox-event-item ${eventSlug === event.slug ? 'selected' : ''}`}
														onClick={() => {
															setAttributes({
																eventSlug: event.slug,
																eventTitle: event.title,
															});
														}}
													>
														{event.event_image && (
															<img
																src={`https://api.tixfox.co/storage/${event.event_image}`}
																alt={event.title}
																className="tixfox-event-image"
															/>
														)}
														<div className="tixfox-event-info">
															<h5>{event.title}</h5>
															<p className="tixfox-event-date">
																{new Date(event.start_time).toLocaleDateString()}
															</p>
															<p className="tixfox-event-location">{event.address}</p>
														</div>
													</div>
												))}
											</div>
										) : (
											<div className="tixfox-empty-state">
												<h4>{__('No Live Events Found', 'tixfox')}</h4>
												<p>{__('Click the button below to create an event, then refresh to see your event.', 'tixfox')}</p>
												<div className="tixfox-empty-actions">
													<a href="https://tixfox.co/create" target="_blank" rel="noopener noreferrer" className="tixfox-cta">
														{__('Create an Event', 'tixfox')}
													</a>
													<button
														className="tixfox-refresh"
														onClick={() => {
															setLoading(true);
															apiFetch({ path: '/tixfox/v1/events' })
																.then((data) => {
																	setEvents(data);
																	setLoading(false);
																})
																.catch((err) => {
																	setError(err.message);
																	setLoading(false);
																});
														}}
													>
														{__('Refresh', 'tixfox')}
													</button>
												</div>
											</div>
										)
									)}

									{activeTab === 'upcoming' && (
										events.upcoming_events?.length > 0 ? (
											<div className="tixfox-events-list">
												{events.upcoming_events.map((event) => (
													<div
														key={event.slug}
														className={`tixfox-event-item ${eventSlug === event.slug ? 'selected' : ''}`}
														onClick={() => {
															setAttributes({
																eventSlug: event.slug,
																eventTitle: event.title,
															});
														}}
													>
														{event.event_image && (
															<img
																src={`https://api.tixfox.co/storage/${event.event_image}`}
																alt={event.title}
																className="tixfox-event-image"
															/>
														)}
														<div className="tixfox-event-info">
															<h5>{event.title}</h5>
															<p className="tixfox-event-date">
																{new Date(event.start_time).toLocaleDateString()}
															</p>
															<p className="tixfox-event-location">{event.address}</p>
														</div>
													</div>
												))}
											</div>
										) : (
											<div className="tixfox-empty-state">
												<h4>{__('No Upcoming Events Found', 'tixfox')}</h4>
												<p>{__('Click the button below to create an event, then refresh to see your event.', 'tixfox')}</p>
												<div className="tixfox-empty-actions">
													<a href="https://tixfox.co/create" target="_blank" rel="noopener noreferrer" className="tixfox-cta">
														{__('Create an Event', 'tixfox')}
													</a>
													<button
														className="tixfox-refresh"
														onClick={() => {
															setLoading(true);
															apiFetch({ path: '/tixfox/v1/events' })
																.then((data) => {
																	setEvents(data);
																	setLoading(false);
																})
																.catch((err) => {
																	setError(err.message);
																	setLoading(false);
																});
														}}
													>
														{__('Refresh', 'tixfox')}
													</button>
												</div>
											</div>
										)
									)}

									{activeTab === 'past' && (
										events.past_events?.length > 0 ? (
											<div className="tixfox-events-list">
												{events.past_events.map((event) => (
													<div
														key={event.slug}
														className={`tixfox-event-item ${eventSlug === event.slug ? 'selected' : ''}`}
														onClick={() => {
															setAttributes({
																eventSlug: event.slug,
																eventTitle: event.title,
															});
														}}
													>
														{event.event_image && (
															<img
																src={`https://api.tixfox.co/storage/${event.event_image}`}
																alt={event.title}
																className="tixfox-event-image"
															/>
														)}
														<div className="tixfox-event-info">
															<h5>{event.title}</h5>
															<p className="tixfox-event-date">
																{new Date(event.start_time).toLocaleDateString()}
															</p>
															<p className="tixfox-event-location">{event.address}</p>
														</div>
													</div>
												))}
											</div>
										) : (
											<div className="tixfox-empty-state">
												<h4>{__('No Past Events Found', 'tixfox')}</h4>
												<p>{__('Click the button below to create an event, then refresh to see your event.', 'tixfox')}</p>
												<div className="tixfox-empty-actions">
													<a href="https://tixfox.co/create" target="_blank" rel="noopener noreferrer" className="tixfox-cta">
														{__('Create an Event', 'tixfox')}
													</a>
													<button
														className="tixfox-refresh"
														onClick={() => {
															setLoading(true);
															apiFetch({ path: '/tixfox/v1/events' })
																.then((data) => {
																	setEvents(data);
																	setLoading(false);
																})
																.catch((err) => {
																	setError(err.message);
																	setLoading(false);
																});
														}}
													>
														{__('Refresh', 'tixfox')}
													</button>
												</div>
											</div>
										)
									)}
								</div>
							</div>
						)}
					</PanelBody>

					{eventSlug && (
						<>
							<PanelBody title={__('Button Content', 'tixfox')} initialOpen={false}>
								<TextControl
									label={__('Button Text', 'tixfox')}
									value={buttonText}
									onChange={(value) => setAttributes({ buttonText: value })}
								/>
								<ToggleControl
									label={__('Show Icon', 'tixfox')}
									checked={showIcon}
									onChange={(value) => setAttributes({ showIcon: value })}
									help={__('Display ticket icon before button text', 'tixfox')}
								/>
							</PanelBody>

							<PanelBody title={__('Button Style', 'tixfox')} initialOpen={false}>
								<RangeControl
									label={__('Button Size (px)', 'tixfox')}
									value={buttonSize}
									onChange={(value) => setAttributes({ buttonSize: value })}
									min={12}
									max={24}
									help={__('Controls the font size of the button', 'tixfox')}
								/>

								<RangeControl
									label={__('Border Radius (px)', 'tixfox')}
									value={borderRadius}
									onChange={(value) => setAttributes({ borderRadius: value })}
									min={0}
									max={50}
								/>

								<p style={{ marginTop: '16px', marginBottom: '8px', fontWeight: '500' }}>
									{__('Background Color', 'tixfox')}
								</p>
								<ColorPalette
									value={backgroundColor}
									onChange={(value) => setAttributes({ backgroundColor: value || '#2271b1' })}
									colors={[
										{ name: 'Blue', color: '#2271b1' },
										{ name: 'Green', color: '#16a34a' },
										{ name: 'Red', color: '#dc2626' },
										{ name: 'Orange', color: '#ea580c' },
										{ name: 'Purple', color: '#9333ea' },
										{ name: 'Black', color: '#000000' },
									]}
								/>

								<p style={{ marginTop: '16px', marginBottom: '8px', fontWeight: '500' }}>
									{__('Text Color', 'tixfox')}
								</p>
								<ColorPalette
									value={textColor}
									onChange={(value) => setAttributes({ textColor: value || '#ffffff' })}
									colors={[
										{ name: 'White', color: '#ffffff' },
										{ name: 'Black', color: '#000000' },
										{ name: 'Gray', color: '#6b7280' },
									]}
								/>
							</PanelBody>
						</>
					)}
				</InspectorControls>

				{!eventSlug ? (
					<Placeholder
						icon={<Icon icon={payment} />}
						label={__('TixFox Event Button', 'tixfox')}
						instructions={__('Select an event from the right panel to create a ticket button.', 'tixfox')}
					/>
				) : (
					<div className="tixfox-button-preview">
						<button 
							className="tixfox-event-button"
							style={{
								backgroundColor: backgroundColor,
								color: textColor,
								borderRadius: `${borderRadius}px`,
								fontSize: `${buttonSize}px`,
								padding: `${buttonSize * 0.75}px ${buttonSize * 1.5}px`,
							}}
							type="button"
						>
							{showIcon && <Icon icon={cart} style={{ marginRight: '8px', fill: textColor }} />}
							{buttonText}
						</button>
						<p className="tixfox-button-hint">
							{__('Selected:', 'tixfox')} {eventTitle}
						</p>
					</div>
				)}
			</div>
		);
	},

	save: ({ attributes }) => {
		const { eventSlug, eventTitle, buttonText, backgroundColor, textColor, buttonSize, borderRadius, showIcon } = attributes;

		if (!eventSlug) return null;

		return (
			<div {...useBlockProps.save()}>
				<button
					className="tixfox-event-button"
					style={{
						backgroundColor: backgroundColor,
						color: textColor,
						borderRadius: `${borderRadius}px`,
						fontSize: `${buttonSize}px`,
						padding: `${buttonSize * 0.75}px ${buttonSize * 1.5}px`,
					}}
					data-event-slug={eventSlug}
					data-event-title={eventTitle}
					data-show-icon={showIcon}
					type="button"
				>
					{showIcon && <Icon icon={cart} style={{ marginRight: '8px', fill: textColor }} />}
					{buttonText}
				</button>
			</div>
		);
	},

	deprecated: [
		{
			attributes: {
				eventSlug: {
					type: 'string',
					default: '',
				},
				eventTitle: {
					type: 'string',
					default: '',
				},
				buttonText: {
					type: 'string',
					default: 'Buy Tickets',
				},
				backgroundColor: {
					type: 'string',
					default: '#2271b1',
				},
				textColor: {
					type: 'string',
					default: '#ffffff',
				},
				buttonSize: {
					type: 'string',
					default: 'medium',
				},
				borderRadius: {
					type: 'number',
					default: 4,
				},
				showIcon: {
					type: 'boolean',
					default: true,
				},
			},
			migrate: (attributes) => {
				const sizeMap = {
					small: 14,
					medium: 16,
					large: 18,
				};
				return {
					...attributes,
					buttonSize: sizeMap[attributes.buttonSize] || 16,
					borderRadius: 8,
				};
			},
			save: ({ attributes }) => {
				const { eventSlug, eventTitle, buttonText, backgroundColor, textColor, buttonSize, borderRadius, showIcon } = attributes;

				if (!eventSlug) return null;

				return (
					<div {...useBlockProps.save()}>
						<button
							className={`tixfox-event-button tixfox-size-${buttonSize}`}
							style={{
								backgroundColor: backgroundColor,
								color: textColor,
								borderRadius: `${borderRadius}px`,
							}}
							data-event-slug={eventSlug}
							data-event-title={eventTitle}
							data-show-icon={showIcon}
							type="button"
						>
							{showIcon && (
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ marginRight: '8px', display: 'inline-block', verticalAlign: 'middle' }}>
									<path d="M3 10h18M3 14h18M8 18h8M7 3h10c1.1 0 2 .9 2 2v14c0 1.1-.9 2-2 2H7c-1.1 0-2-.9-2-2V5c0-1.1.9-2 2-2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
								</svg>
							)}
							{buttonText}
						</button>
					</div>
				);
			},
		},
		{
			attributes: {
				eventSlug: {
					type: 'string',
					default: '',
				},
				eventTitle: {
					type: 'string',
					default: '',
				},
				buttonText: {
					type: 'string',
					default: 'Buy Tickets',
				},
			},
			save: ({ attributes }) => {
				const { eventSlug, eventTitle, buttonText } = attributes;

				if (!eventSlug) return null;

				return (
					<div {...useBlockProps.save()}>
						<button
							className="tixfox-event-button"
							data-event-slug={eventSlug}
							data-event-title={eventTitle}
							type="button"
						>
							{buttonText}
						</button>
					</div>
				);
			},
		},
	],
});
